import { SERVICES } from "../lib/constants";
import { SectionHeader, BookNowButton } from "./Shared";
import { ArrowUpRight, Beaker, Sparkles, Droplets, Wind, PartyPopper, Home } from "lucide-react";

const ICONS = {
  residential: Home,
  airbnb: Sparkles,
  laundry: Droplets,
  events: PartyPopper,
  windows: Wind,
  pressure: Beaker,
};

const Card = ({ service }) => {
  const Icon = ICONS[service.id] || Beaker;
  const hasImage = !!service.image;
  return (
    <a
      href="#contact"
      data-testid={`service-card-${service.id}`}
      className="group relative flex flex-col overflow-hidden rounded-3xl border border-white/10 bg-[#0A0A0A] hover:border-[#00E5FF]/50 transition-all duration-500 h-full"
    >
      {hasImage && (
        <div className="relative h-48 sm:h-56 overflow-hidden">
          <img
            src={service.image}
            alt={service.title}
            className={`h-full w-full object-cover transition-transform duration-700 group-hover:scale-105 ${
              service.comingSoon ? "opacity-40 grayscale-[40%]" : ""
            }`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
          <div className="absolute top-4 left-4 rounded-full bg-black/60 backdrop-blur-md border border-white/10 px-3 py-1 text-[10px] font-mono uppercase tracking-[0.2em] text-white/70">
            {service.code}
          </div>
          {service.comingSoon && (
            <div
              data-testid={`coming-soon-${service.id}`}
              className="absolute top-4 right-4 inline-flex items-center gap-1.5 rounded-full bg-[#00E5FF] text-black px-3 py-1 text-[10px] font-mono uppercase tracking-[0.2em] font-semibold"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-black animate-pulse" />
              Coming Soon
            </div>
          )}
        </div>
      )}
      <div className="relative flex-1 p-6 sm:p-8 flex flex-col">
        {!hasImage && (
          <div className="mb-4 flex items-center justify-between">
            <span className="rounded-full bg-[#00E5FF]/10 text-[#00E5FF] px-3 py-1 text-[10px] font-mono uppercase tracking-[0.2em]">
              {service.code}
            </span>
            <Icon size={20} className="text-[#00E5FF]" />
          </div>
        )}
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display text-xl sm:text-2xl font-semibold tracking-tight">
            {service.title}
          </h3>
          <ArrowUpRight
            size={20}
            className="text-white/40 group-hover:text-[#00E5FF] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all shrink-0"
          />
        </div>
        <p className="mt-3 text-sm text-white/60 font-body leading-relaxed">
          {service.blurb}
        </p>
        <ul className="mt-5 space-y-1.5">
          {service.bullets.map((b) => (
            <li
              key={b}
              className="flex items-center gap-2 text-xs text-white/50 font-body"
            >
              <span className="h-px w-4 bg-[#00E5FF]/60" />
              {b}
            </li>
          ))}
        </ul>
      </div>
    </a>
  );
};

export const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
          <SectionHeader
            eyebrow="Services · the formulary"
            title={
              <>
                Five protocols.{" "}
                <span className="text-white/50 italic font-light">
                  One obsessive standard.
                </span>
              </>
            }
            sub="From everyday maintenance to full exterior restoration — each service is a measured recipe, not a guess."
          />
          <BookNowButton variant="secondary" testId="services-book-now" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {SERVICES.map((s) => (
            <Card key={s.id} service={s} />
          ))}
        </div>
      </div>
    </section>
  );
};
